import { useEffect } from 'react'
import { GuestLayout } from '@/layouts/GuestLayout'
import { PoiIcon } from '@/lib/poi-icon'
import { trackEvent } from '@/lib/trackEngagement'
import type { GuestPoi, GuestProperty } from '@/lib/types'

interface Props {
  share_token: string
  property: GuestProperty
  poi: GuestPoi
}

const SAFE_URL = /^https?:\/\//i

function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null
  return SAFE_URL.test(url) ? url : null
}

function hostInitials(name: string | null): string {
  if (!name) return '?'
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function ContactRow({ icon, label, detail, href, onClickTrack }: { icon: React.ReactNode; label: string; detail: string | null; href?: string | null; onClickTrack?: () => void }) {
  const Tag = href ? 'a' : 'div'
  return (
    <Tag
      href={href ?? undefined}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      onClick={onClickTrack}
      className="g-tap"
      style={{
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14,
        borderBottom: '1px solid var(--g-line)', textDecoration: 'none', color: 'inherit', cursor: href ? 'pointer' : 'default',
      }}
    >
      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--sand-100, #F4ECE0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>{label}</div>
        {detail && <div style={{ fontSize: 12, color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>}
      </div>
      {href && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6M10 14L21 3"/>
        </svg>
      )}
    </Tag>
  )
}

export default function PoiDetail({ share_token, property, poi }: Props) {
  useEffect(() => { trackEvent(share_token, 'poi_view', { poi_id: poi.id }) }, [])
  const initials = hostInitials(property.host_name)
  const brandSoft = property.brand_color + '40'

  const primaryCta = safeUrl(poi.booking_url)
    ? { href: poi.booking_url!, label: 'Reserve a table' }
    : poi.lat && poi.lng
    ? { href: `https://maps.google.com/?q=${poi.lat},${poi.lng}`, label: 'Get directions' }
    : safeUrl(poi.website_url)
    ? { href: poi.website_url!, label: 'Visit website' }
    : null

  const category     = poi.categories[0]?.name ?? null
  const categoryIcon = poi.categories[0]?.icon ?? null

  return (
    <GuestLayout brandColor={property.brand_color}>
      <div style={{ background: 'var(--g-bg, #FBF7F1)', paddingBottom: 40 }}>

        {/* Hero — stripe always visible as instant fallback; photo overlays when available */}
        <div className="g-poi-stripe" style={{ position: 'relative', height: 340 }}>
          {poi.image_url && (
            <img
              src={poi.image_url}
              alt=""
              aria-hidden="true"
              loading="eager"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
          )}
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, var(--g-bg, #FBF7F1) 100%)',
            }}
          />

          {/* Back button */}
          <a
            href={`/g/${share_token}`}
            aria-label="Back to guide"
            className="g-tap"
            style={{
              position: 'absolute', top: 'calc(56px + env(safe-area-inset-top))', left: 16,
              width: 40, height: 40, borderRadius: 999,
              background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1F1A14" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </a>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px', marginTop: -24, position: 'relative' }}>

          {/* Meta */}
          {category && (
            <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
              {categoryIcon && <PoiIcon name={categoryIcon} size={12} />}
              {category}
            </div>
          )}

          {/* Name */}
          <h1
            className="g-serif"
            style={{ fontSize: 44, lineHeight: 0.98, letterSpacing: '-0.02em', color: 'var(--ink-900)', marginBottom: 18 }}
          >
            {poi.name}
          </h1>

          {/* Host note */}
          {poi.host_note && (
            <div
              style={{
                background: brandSoft,
                borderRadius: 16, padding: 18, marginBottom: 22,
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <div
                  style={{
                    width: 28, height: 28, borderRadius: 999,
                    background: property.brand_color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
                <div className="g-mono" style={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>
                  {property.host_name ? `Note from ${property.host_name}` : 'Host note'}
                </div>
              </div>
              <p className="g-serif" style={{ fontSize: 19, lineHeight: 1.35, color: 'var(--ink-900)', fontStyle: 'italic', margin: 0 }}>
                "{poi.host_note}"
              </p>
            </div>
          )}

          {/* Description */}
          {poi.description && (
            <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--ink-700)', marginBottom: 22 }}>
              {poi.description}
            </p>
          )}

          {/* Category tags */}
          {poi.categories.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 26 }}>
              {poi.categories.map(c => (
                <span
                  key={c.name}
                  style={{
                    padding: '5px 11px', borderRadius: 999,
                    background: 'var(--sand-100, #F4ECE0)', color: 'var(--ink-700)',
                    fontSize: 11.5, fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  {c.icon && <PoiIcon name={c.icon} size={12} />}
                  {c.name}
                </span>
              ))}
            </div>
          )}

          {/* Primary CTA */}
          {primaryCta && (
            <a
              href={primaryCta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="g-tap"
              onClick={() => {
                const isBooking = !!safeUrl(poi.booking_url)
                trackEvent(share_token, isBooking ? 'booking_click' : 'directions_click', { poi_id: poi.id })
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: 18, borderRadius: 14,
                background: 'var(--ink-900, #1F1A14)', color: 'var(--sand-50, #FBF7F1)',
                fontSize: 15, fontWeight: 500, letterSpacing: '-0.01em',
                textDecoration: 'none', marginBottom: 8,
              }}
            >
              <span>{primaryCta.label}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6M10 14L21 3"/>
              </svg>
            </a>
          )}

          {/* Secondary contact list */}
          <div style={{ background: 'var(--g-surface, #fff)', borderRadius: 16, border: '1px solid var(--g-line)', marginTop: 12, overflow: 'hidden' }}>
            {poi.address && (
              <ContactRow
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z"/><circle cx="12" cy="10" r="3"/></svg>}
                label="Get directions"
                detail={poi.address}
                href={poi.google_maps_url ?? (poi.lat && poi.lng ? `https://maps.google.com/?q=${poi.lat},${poi.lng}` : null)}
                onClickTrack={
                  // Only track here when primaryCta is not already a directions link to avoid double-counting
                  primaryCta?.label === 'Get directions' ? undefined
                    : () => trackEvent(share_token, 'directions_click', { poi_id: poi.id })
                }
              />
            )}
            {poi.phone && (
              <ContactRow
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z"/></svg>}
                label="Call"
                detail={poi.phone}
                href={`tel:${poi.phone}`}
              />
            )}
            {safeUrl(poi.website_url) && (
              <ContactRow
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20"/></svg>}
                label="Website"
                detail={poi.website_url}
                href={poi.website_url}
              />
            )}
          </div>
        </div>
      </div>
    </GuestLayout>
  )
}
